import React, { useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import StepsModal from '@components/StepsModal';
import api from '@services/api';
import CustomerStep from '@components/CustomerStep';
import { formatPricePerMB, toBytes } from '@utils/formatters';
import { TypeOfContractStep, ConfigStep } from './NewContractSteps';
import { UnitType, IConfigStepValues } from './NewContractSteps/ConfigStep';

type Flag = 'S' | 'N';
type TypeOfContract = 1 | 2 | 4 | undefined;

interface INewContractModal {
  visible: boolean;
  onClose: Function;
  mutateNewContract: Function;
}

const NewContractModal: React.FC<INewContractModal> = ({
  visible,
  onClose,
  mutateNewContract,
}) => {
  const { t } = useTranslation(['contracts', 'attributes']);

  const [customerId, setCustomerId] = useState<number>();
  const [sharingType, setSharingType] = useState<number>();
  const [typeOfContract, setTypeOfContract] = useState<TypeOfContract>();
  const [payPerUseFlag, setPayPerUseFlag] = useState<Flag>();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [configValues, setConfigValues] = useState<IConfigStepValues>();
  const [units, setUnits] = useState<{
    franchise: UnitType;
    execPrice: UnitType;
  }>({
    franchise: 'MB',
    execPrice: 'MB',
  });

  const confirmDisabled =
    !configValues?.planDesc ||
    configValues?.planDesc === '' ||
    (payPerUseFlag === 'N' && !configValues?.motOperator) ||
    (configValues?.loyaltyType === 2 && !configValues?.loyaltyTime) ||
    !configValues?.planType ||
    (configValues?.planType === 'BAIT' && !configValues?.iscaDurationInDays);

  const createContract = useCallback(
    (payload: any) => api.post('/service-proxy/broker/virtual-plan', payload),
    [],
  );

  const handleSaveContract = useCallback(async () => {
    const payload = {
      customerId,
      defaultOverflowPricing: [],
      defaultOperatorPricing: [],
      operatorDiffChargeFlag: 'N',
      payPerUseFlag,
      sharingType,
      ...configValues,
      franchise: toBytes(configValues?.franchise ?? 0, units?.franchise),
      megabyteExcPrice:
        formatPricePerMB(units?.execPrice, configValues?.megabyteExcPrice) ?? 0,
      iscaMaxRenewal: configValues?.iscaMaxRenewal ?? 0,
      loyaltyType: configValues?.loyaltyType ?? 0,
    };

    if (configValues?.loyaltyType !== 2) {
      delete payload.loyaltyTime;
    }

    setConfirmLoading(true);
    try {
      await createContract(payload);
      mutateNewContract(payload);
      message.success(t('msgs.success'));
      onClose();
    } catch {
      message.error(t('msgs.error'));
    }
    setConfirmLoading(false);
  }, [
    customerId,
    payPerUseFlag,
    sharingType,
    configValues,
    units?.franchise,
    units?.execPrice,
    createContract,
    mutateNewContract,
    t,
    onClose,
  ]);

  const handleSelectContractTypeChange = useCallback(
    (value: TypeOfContract) => {
      setTypeOfContract(value);
      // setChargeByUseData(undefined);
      if (value === 4) {
        setSharingType(1);
        setPayPerUseFlag('S');
      } else {
        setSharingType(value);
        setPayPerUseFlag('N');
      }
    },
    [],
  );

  const newContractSteps = useMemo(
    () => [
      {
        title: t('attributes:customer'),
        disabled: !customerId,
        content: (
          <CustomerStep
            value={customerId}
            onChange={value => setCustomerId(value)}
          />
        ),
      },
      {
        title: t('newContract.typeOfContract'),
        disabled: !typeOfContract,
        content: (
          <TypeOfContractStep
            values={{
              typeOfContract,
            }}
            onChange={values => {
              handleSelectContractTypeChange(values?.typeOfContract);
            }}
          />
        ),
      },
      {
        title: t('newContract.config'),
        content: (
          <ConfigStep
            values={configValues}
            chargedByUse={payPerUseFlag}
            sharingType={sharingType}
            onChange={setConfigValues}
            units={units}
            setUnits={setUnits}
          />
        ),
      },
    ],
    [
      t,
      customerId,
      typeOfContract,
      configValues,
      payPerUseFlag,
      sharingType,
      units,
      handleSelectContractTypeChange,
    ],
  );

  return (
    <StepsModal
      title={t('newContract.title')}
      width="700px"
      onConfirm={handleSaveContract}
      isOpen={visible}
      onClose={() => onClose()}
      allowStepsLine
      steps={newContractSteps}
      confirmLoading={confirmLoading}
      confirmDisabled={confirmDisabled}
    />
  );
};

export default NewContractModal;
